import { InvoiceStatus } from "@wingspanhq/wingspan-api-sdk";
import React from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { queriesInvoices } from "@query";
import { INVOICING_PATH } from "../../modules/Invoicing";
import { WSQueries } from "../../query/WSQuery";
import { InvoicesForm } from "../components/InvoicesForm/InvoicesForm";
import { useDefaultInvoiceSubject } from "../utils";
import {
  convertToLineItemFormDataFromV3,
  creditFeeToFormDataFromV3,
  defaultFrequency,
  invoiceSplitsToFormData,
  lateFeeToFormDataFromV3,
  paymentMethodsToFormDataFromV3
} from "../utils/invoicesFormUtils";

type Props = RouteComponentProps<{ invoiceId: string }>;

export const InvoicesInvoiceEdit: React.FC<Props> = ({ match }) => {
  const history = useHistory();
  const invoiceId = match.params.invoiceId;
  const queryInvoice = queriesInvoices.getInvoice.useQuery(
    { invoiceId },
    {
      onSuccess: invoice => {
        // Work-log-backed invoices edit through their own screen. v3 keeps the
        // originating work log on the invoice rather than per line item.
        if (invoice.workLogId) {
          history.push(INVOICING_PATH + "/" + invoiceId + "/edit-work-log");
        }
      }
    }
  );

  const defaultSubject = useDefaultInvoiceSubject();

  return (
    <WSQueries queries={{ queryInvoice }}>
      {({ queryInvoice: { data: invoice } }) => (
        <InvoicesForm
          context={{ invoice }}
          defaultValues={{
            subject: String(invoice.metadata?.subject || defaultSubject),
            payer: {
              payerId: invoice.payerId || null,
              payerEngagementId: invoice.payerEngagementId || null,
              emailsCC: []
            },
            purchaseOrderNumber: invoice.metadata?.purchaseOrderNumber || "",
            lineItems: (invoice?.lineItems || []).map(
              convertToLineItemFormDataFromV3
            ),
            other: {
              projectName: String(invoice?.metadata?.projectName || ""),
              notes: invoice?.notes || "",
              // Attachments are disabled (no upload path on either API).
              attachments: []
            },
            due: "custom",
            customDueDate: invoice?.dueDate || new Date(),
            includesLateFee:
              !!invoice.lateFeeHandling && !!invoice.lateFeeHandling?.frequency,
            lateFee: lateFeeToFormDataFromV3(invoice.lateFeeHandling),
            send: {
              type: "immediately",
              date: new Date()
            },
            recurring: false,
            frequency: defaultFrequency,

            splits: invoiceSplitsToFormData(invoice.splits),
            paymentMethods: paymentMethodsToFormDataFromV3(
              invoice.acceptedPaymentMethods
            ),
            advanced: {
              sendEmails: !!invoice?.notificationPreferences?.shouldSendInvoice,
              sendReminders:
                !!invoice?.notificationPreferences?.shouldSendReminders,
              creditFeeHandling: creditFeeToFormDataFromV3(
                invoice.creditFeeHandling
              ),
              date:
                (invoice?.metadata?.invoiceDate
                  ? new Date(invoice.metadata.invoiceDate)
                  : undefined) ||
                (invoice?.status === InvoiceStatus.CREATED
                  ? new Date()
                  : invoice.events?.createdAt) ||
                new Date()
            }
          }}
        />
      )}
    </WSQueries>
  );
};
